import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAdAccount } from "../lib/adAccount";
import {
  ChatTeardropDots, ChartBar, MegaphoneSimple, Gear, Users, Lightning,
  SignOut, CaretDown, CaretUpDown, Pulse, ImageSquare, UsersThree, ClipboardText
} from "@phosphor-icons/react";
import { useState } from "react";
import NotificationBell from "./NotificationBell";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { accounts, selected, select } = useAdAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navItems = [
    { to: "/", label: "Chat", icon: ChatTeardropDots, testid: "nav-chat" },
    { to: "/dashboard", label: "Dashboard", icon: ChartBar, testid: "nav-dashboard" },
    { to: "/campaigns", label: "Campaigns", icon: MegaphoneSimple, testid: "nav-campaigns" },
    { to: "/audience", label: "Audience", icon: UsersThree, testid: "nav-audience" },
    { to: "/creatives", label: "Creatives", icon: ImageSquare, testid: "nav-creatives" },
    { to: "/audit", label: "Audit Log", icon: ClipboardText, testid: "nav-audit" },
  ];
  if (user?.role === "admin") {
    navItems.push({ to: "/users", label: "Users", icon: Users, testid: "nav-users" });
    navItems.push({ to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" });
  }

  const selectedAcct = accounts.find(a => a.id === selected);

  return (
    <div className="min-h-screen flex bg-[#050505] text-zinc-50">
      {/* Sidebar */}
      <aside className="w-60 border-r border-zinc-900 flex flex-col bg-[#070707]">
        {/* Logo */}
        <div className="h-14 px-4 flex items-center gap-2 border-b border-zinc-900">
          <div className="w-7 h-7 bg-orange-500 flex items-center justify-center">
            <Lightning size={15} weight="fill" className="text-zinc-950" />
          </div>
          <span className="font-display font-bold text-sm tracking-tight">UNDERADS<span className="text-orange-500"> METAVERSE</span></span>
        </div>

        {/* Ad account selector */}
        <div className="p-3 border-b border-zinc-900">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-600 mb-1.5 px-1">Ad Account</div>
          <div className="relative">
            <button
              data-testid="ad-account-selector"
              onClick={() => setAccountMenuOpen(!accountMenuOpen)}
              className="w-full bg-[#0a0a0a] border border-zinc-800 hover:border-zinc-700 px-2.5 py-2 text-xs flex items-center justify-between text-left transition-colors"
            >
              <div className="flex-1 truncate">
                <div className="font-medium text-zinc-100 truncate text-[13px]">{selectedAcct?.name || "—"}</div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{selectedAcct?.id || "no account"}</div>
              </div>
              <CaretUpDown size={12} className="text-zinc-500 shrink-0 ml-1" />
            </button>
            {accountMenuOpen && (
              <div className="absolute left-0 right-0 mt-1 bg-[#0a0a0a] border border-zinc-800 z-50 shadow-2xl">
                {accounts.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`ad-account-option-${a.id}`}
                    onClick={() => { select(a.id); setAccountMenuOpen(false); }}
                    className={`w-full px-2.5 py-2 text-left text-xs hover:bg-zinc-900 transition-colors ${a.id === selected ? "bg-zinc-900/60 border-l-2 border-orange-500" : ""}`}
                  >
                    <div className="font-medium text-zinc-100 text-[13px]">{a.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{a.id} · {a.currency}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2 space-y-0.5">
          {navItems.map(({ to, label, icon: Icon, testid }) => {
            const active = location.pathname === to || (to === "/" && location.pathname.startsWith("/chat"));
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-2.5 px-2.5 py-2 text-sm transition-colors ${
                  active ? "bg-zinc-900 text-zinc-50 border-l-2 border-orange-500" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40 border-l-2 border-transparent"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} className={active ? "text-orange-500" : ""} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Notifications + Mode badge */}
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center justify-between bg-[#0a0a0a] border border-zinc-800 px-2.5 py-1.5">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Alerts</div>
            <NotificationBell />
          </div>
          <div className={`bg-[#0a0a0a] border ${user?.meta_mode_override === "live" ? "border-orange-500/40" : "border-emerald-500/20"} px-2.5 py-2 flex items-center gap-2`}>
            <Pulse size={12} weight="fill" className={user?.meta_mode_override === "live" ? "text-orange-500 animate-pulse-orange" : "text-emerald-400 animate-pulse-orange"} />
            <div>
              <div className={`text-[10px] font-mono tracking-[0.2em] uppercase ${user?.meta_mode_override === "live" ? "text-orange-400" : "text-emerald-400"}`}>
                {user?.meta_mode_override === "live" ? "Live Mode" : "Sandbox Mode"}
              </div>
              <div className="text-[10px] text-zinc-500">{user?.meta_mode_override === "live" ? "Real Meta API" : "Mock Meta data"}</div>
            </div>
          </div>
        </div>

        {/* User menu */}
        <div className="border-t border-zinc-900 p-3 relative">
          <button
            data-testid="user-menu-button"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-2.5 hover:bg-zinc-900/40 px-1.5 py-1.5 transition-colors"
          >
            <div className={`w-7 h-7 ${user?.role === "admin" ? "bg-orange-500" : "bg-zinc-800"} flex items-center justify-center text-xs font-bold ${user?.role === "admin" ? "text-zinc-950" : "text-zinc-200"}`}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-[13px] font-medium truncate">{user?.display_name || user?.username}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{user?.role}</div>
            </div>
            <CaretDown size={11} className="text-zinc-500" />
          </button>
          {userMenuOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-1 bg-[#0a0a0a] border border-zinc-800 shadow-2xl">
              <button
                data-testid="logout-button"
                onClick={logout}
                className="w-full px-3 py-2 text-left text-xs hover:bg-zinc-900 flex items-center gap-2 text-zinc-300"
              >
                <SignOut size={13} /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-hidden flex flex-col">{children}</main>
    </div>
  );
}
